import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import BedCard from './BedCard';
import { Info } from 'lucide-react';

const BedZone = ({ zone, beds }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `zone-${zone.id}`,
  });
  
  // Calculate zone statistics
  const stats = {
    occupied: beds.filter(b => b.status === 'occupied').length,
    available: beds.filter(b => b.status === 'available').length,
    cleaning: beds.filter(b => b.status === 'cleaning').length,
    maintenance: beds.filter(b => b.status === 'maintenance').length
  };
  
  const occupancyRate = beds.length > 0 ? ((stats.occupied / beds.length) * 100).toFixed(0) : '0';
  
  // Calculate bed range from actual beds if not provided
  const bedNumbers = beds.map(bed => parseInt(bed.number)).filter(num => !isNaN(num)).sort((a, b) => a - b);
  const bedRange = zone.bedRange || (bedNumbers.length > 0 ? { 
    start: Math.min(...bedNumbers), 
    end: Math.max(...bedNumbers) 
  } : null);
  
  return (
    <div 
      ref={setNodeRef}
      className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
        isOver ? 'border-blue-400 shadow-lg' : 'border-gray-200'
      }`}
    >
      {/* Zone Header */}
      <div 
        className="p-4 border-b border-gray-200"
        style={{ backgroundColor: `${zone.color}10` }}
      >
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{zone.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{zone.description}</p>
            <div className="flex items-center space-x-4 mt-2">
              {bedRange && (
                <span className="text-sm text-gray-500">
                  Beds {bedRange.start}-{bedRange.end}
                </span>
              )}
              <span className="text-sm text-gray-500">
                ESI {zone.esiLevels?.join(', ') || 'All'}
              </span>
              <span className="text-sm text-gray-500">
                Capacity: {zone.capacity || beds.length}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div 
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: zone.color }}
            >
              {occupancyRate}% Full
            </div>
            <div className="flex space-x-3 mt-2 text-xs text-gray-500">
              <span>{stats.occupied} occupied</span>
              <span>{stats.available} available</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Beds Grid */}
      <div className="p-4">
        <div className={`grid gap-2 ${
          (zone.capacity || beds.length) <= 10 ? 'grid-cols-5' :
          (zone.capacity || beds.length) <= 50 ? 'grid-cols-10' :
          'grid-cols-10 lg:grid-cols-15 xl:grid-cols-20'
        }`}>
          {beds.map((bed) => (
            <BedCard 
              key={bed.id} 
              bed={bed} 
              zoneColor={zone.color}
            />
          ))}
        </div>
        
        {beds.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Info className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">No beds match your search in this zone</p>
          </div>
        )}
      </div>
      
      {/* Zone Footer Stats */}
      {stats.cleaning > 0 || stats.maintenance > 0 ? (
        <div className="px-4 pb-4">
          <div className="flex space-x-4 text-xs text-gray-500">
            {stats.cleaning > 0 && (
              <span className="flex items-center">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mr-1"></div>
                {stats.cleaning} cleaning
              </span>
            )}
            {stats.maintenance > 0 && (
              <span className="flex items-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                {stats.maintenance} maintenance
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BedZone; 